import { IQuestionnaireGroup, IQuestionnaireItem, IQuestionnaireOverviewItem, Messgeraete } from '@interfaces/interfaces';
import { SpecialQuestionItem, SpecialQuestionType } from '@interfaces/questions';
import { Component, EventEmitter, Inject, Injectable, InjectionToken, Input, Optional, Output, SimpleChanges, Type } from '@angular/core';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import { Router } from '@angular/router';
import { participantPages } from '@app/pages/participant/participants.routes';
import { MissingValueService } from '@app/services/missing-value/missing-value.service';
import { SessionItemData } from '@workflow/services/session-item-data';

/**
 * Komponenten-Interface für SpecialQuestions
 */
export interface ISpecialQuestionComponent {
	question: IQuestionnaireItem;
	page: number;
	workflowItemId: string;
	epilogUrl: string;
	prologUrl: string;
	nextUrl: string;
	valid: EventEmitter<boolean>;
	finishedAndLocked: boolean;
	overviewConfigs?: IQuestionnaireOverviewItem[];

	ngOnChanges?: (changes: SimpleChanges) => unknown;
	ngAfterViewInit?: () => unknown;
}

@Component({ template: ''})
export abstract class AbstractSpecialQuestionComponent implements ISpecialQuestionComponent {

	@Input() question: SpecialQuestionItem;
	@Input() page: number;
	@Input() workflowItemId: string;
	@Input() epilogUrl: string;
	@Input() prologUrl: string;
	@Input() nextUrl: string;
	@Input() finishedAndLocked: boolean;
	@Input() overviewConfigs: IQuestionnaireOverviewItem[];

	@Output() valid: EventEmitter<boolean> = new EventEmitter();

	protected constructor(private router: Router) {}

	protected goToNext(): void {
		this.router
			.navigate([participantPages.workflowProcess.base({wfItemId: this.workflowItemId, pageUrl: this.nextUrl})])
			.catch(console.error);
	}

	protected goToEpilog(): void {
		this.router
			.navigate([participantPages.workflowProcess.base({wfItemId: this.workflowItemId, pageUrl: this.epilogUrl})])
			.catch(console.error);
	}

	protected goToProlog(): void {
		this.router
			.navigate([participantPages.workflowProcess.base({wfItemId: this.workflowItemId, pageUrl: this.epilogUrl})])
			.catch(console.error);
	}

}

export interface ISpecialQuestion {
	/**
	 * SpecialQuestion Typ
	 */
	specialQuestionType: SpecialQuestionType,

	/**
	 * Alternative Konfiguration
	 */
	defaultGroupConfiguration: IQuestionnaireGroup

	/**
	 * Test, ob SpecialQuestion vollständig bearbeitet ist
	 *
	 * @param currentValue Daten
	 */
	isCompleted(currentValue: unknown): boolean;

	/**
	 * Komponente für SpecialQuestion
	 */
	component: Type<ISpecialQuestionComponent>;

	/**
	 * Messplatzanforderung
	 */
	requiredDevices: Messgeraete[];

	/**
	 * Prolog benutzt volle Höhe der Seite
	 */
	prologueFullHeight?: boolean;

	title?: string;

	missingValueKeyGroup?: string;

	/**
	 * WizardPages aufbauen
	 *
	 * @param group Fragegrupper
	 * @param specialQuestion Individuelle Frage
	 * @param groupIndex Index der Fragegruppe
	 * @param currentValue Derzeitige Eingabewerte der Fragegruppe
	 */
	buildWizardPages(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem, groupIndex: number, currentValue: unknown): WizardPage[];

	buildEpilogPages?(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem): WizardPage[];
}

/**
 * DependencyInjection Token zum Auffinden der ISpeicalQUestion Implementierungen.
 * Die Implementierungen müssen noch separat in SpecialQuestionModule für das Token registiert werden.
 */
export const SPECIAL_QUESTION_DI_TOKEN = new InjectionToken<ISpecialQuestion>('ISpecialQuestion');

@Injectable({
	providedIn: 'root',
})
export class SpecialQuestionService {

	private lookup: Map<string, ISpecialQuestion>;

	constructor(@Optional() @Inject(SPECIAL_QUESTION_DI_TOKEN) private implementations: ISpecialQuestion[]) {
		this.lookup = new Map<string, ISpecialQuestion>();
		for (const specialQuestion of this.implementations || []) {
			this.lookup.set(specialQuestion.specialQuestionType, specialQuestion);
		}
	}

	public getSpecialQuestionByType(questionType: string): ISpecialQuestion {
		const specialQuestion = this.lookup.get(questionType);
		if (!specialQuestion) {
			console.log(`invalid type ${questionType}`);
		}
		return specialQuestion;
	}

	public getSpecialQuestion(question: SpecialQuestionItem): ISpecialQuestion {
		return this.getSpecialQuestionByType(question?.type);
	}

	public getSpecialQuestionCompleted(group: IQuestionnaireGroup, question: SpecialQuestionItem, currentValue: unknown, sessionItemData: SessionItemData): boolean {
		const specialQuestion = this.getSpecialQuestion(question);
		const baseKey = question.baseKey || question.key;
		if (specialQuestion.missingValueKeyGroup) {
			const missingValueKey = `${baseKey}.${specialQuestion.missingValueKeyGroup}`;
			const isMissingValue = MissingValueService.isMissingValue({ missingValueKey: missingValueKey }, sessionItemData);
			if (isMissingValue) {
				return true;
			}
		}
		const pagesWithMissingValueKey = specialQuestion.buildWizardPages(group, question, 0, currentValue).filter(page => !!page.missingValueKey);
		if (pagesWithMissingValueKey.length) {
			const isMissingValue = pagesWithMissingValueKey.map(page => page.missingValueKey).every(key => {
				const missingValueKey = `${baseKey}.${key}`;
				return MissingValueService.isMissingValue({ missingValueKey: missingValueKey }, sessionItemData);
			});
			if (isMissingValue) {
				return true;
			}
		}
		return specialQuestion?.isCompleted(currentValue);
	}

	public isSpecialQuestion(questionType: string): boolean {
		return this.lookup.has(questionType);
	}

	public getSpecialQuestionTypes(): string[] {
		return Array.from(this.lookup.keys());
	}
}
